import { useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { http, apiError, DIVISIONS } from "../../lib/api";

export const ReviewForm = ({ token, prefill }) => {
  const [f, setF] = useState({ display_name: prefill?.name || "", email: "", company: "", rating: null, division: prefill?.division || "", body: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Please confirm your consent to publish.");
    setBusy(true);
    try {
      if (token) {
        const { email, division, ...rest } = f;
        await http.post(`/review-request/${token}`, rest);
      } else await http.post("/reviews/submit", f);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="border hairline p-10 text-center" data-testid="review-form-success">
        <CheckCircle2 className="w-10 h-10 text-gold mx-auto" />
        <p className="font-display text-3xl mt-4">Thank you for sharing.</p>
        <p className="text-sm text-slate-300 mt-3 max-w-md mx-auto">Your review has been received and will be reviewed before it appears on the website.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="review-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="label" htmlFor="rv-name">Name to display *</label>
          <input id="rv-name" required className="field" value={f.display_name} onChange={set("display_name")} placeholder="e.g. John D." data-testid="review-name-input" />
        </div>
        {!token && (
          <div>
            <label className="label" htmlFor="rv-email">Email (not published) *</label>
            <input id="rv-email" type="email" required className="field" value={f.email} onChange={set("email")} data-testid="review-email-input" />
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="label" htmlFor="rv-company">Company (optional)</label>
          <input id="rv-company" className="field" value={f.company} onChange={set("company")} data-testid="review-company-input" />
        </div>
        <div>
          <label className="label" htmlFor="rv-division">Division (optional)</label>
          <select id="rv-division" className="field" value={f.division} onChange={set("division")} disabled={!!token} data-testid="review-division-select">
            <option value="">Not specified</option>
            {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <span className="label">Rating (optional)</span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onClick={() => setF({ ...f, rating: f.rating === n ? null : n })} className="p-1" aria-label={`${n} stars`} data-testid={`review-rating-${n}`}>
              <Star className={`w-6 h-6 transition-colors ${f.rating && n <= f.rating ? "fill-[var(--gold)] text-gold" : "text-slate-500 hover:text-gold"}`} />
            </button>
          ))}
          {f.rating && <button type="button" className="ml-3 text-xs text-slate-400 underline" onClick={() => setF({ ...f, rating: null })}>Clear</button>}
        </div>
      </div>
      <div>
        <label className="label" htmlFor="rv-body">Your experience *</label>
        <textarea id="rv-body" required minLength={10} rows={5} className="field resize-none" value={f.body} onChange={set("body")} data-testid="review-body-input" />
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-1 accent-[#d4af37] w-4 h-4" data-testid="review-consent-checkbox" />
        <span>I confirm this review reflects my genuine experience and I give Selby Asset Group permission to publish it, with my display name, on its website. I understand reviews are moderated before publication.</span>
      </label>
      <button type="submit" disabled={busy} className="btn-gold" data-testid="review-submit-button">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Review
      </button>
    </form>
  );
};
